import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { locationWindowsRequest } from '../../utils/locationWindows';

export default function LocationWindowsTab({ boxId, value, onChange, devices, gatewayId, readOnly, busy }) {
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    if (boxId) {
      onChange({ boxId, loading: true });
      locationWindowsRequest(boxId).then(result => {
        if (current) onChange({ ...result, boxId, originalWindows: result.windows, dirty: false, loading: false });
      }).catch(error => {
        if (current) onChange({ boxId, loading: false, error: error.message });
      });
    }
    return () => { current = false; };
  }, [boxId, onChange, attempt]);
  if (!boxId) return <Typography sx={{ p: 3 }}>Save this location before configuring windows.</Typography>;
  if (!value || value.boxId !== boxId || value.loading) return <Typography sx={{ p: 3 }}>Loading windows…</Typography>;
  if (value.error) return <Box sx={{ p: 3 }}><Typography color="error">{value.error}</Typography><Button onClick={() => setAttempt(previous => previous + 1)}>Retry</Button></Box>;
  if (!value.available) return <Typography sx={{ p: 3 }}>Window setup is pending. Existing location workflows remain available.</Typography>;
  const rows = value.windows || [];
  const changeRows = windows => onChange({ ...value, windows, dirty: true });
  const update = (index, changes) => changeRows(rows.map((row, i) => i === index ? { ...row, ...changes } : row));
  const remove = index => changeRows(rows.flatMap((row, i) => {
    if (i !== index) return [row];
    // Keep saved rows in the request so the API can retire them and preserve history.
    return row.id ? [{ ...value.originalWindows.find(saved => saved.id === row.id), is_active: false }] : [];
  }));
  const disabled = readOnly || busy;
  const availableDevices = (devices || []).filter(device => device.is_active !== false && device.payment_gateway_id === gatewayId);
  const deviceId = device => device.id || device.local_id;
  const activeCount = rows.filter(row => row.is_active).length;
  const columns = { xs: 'minmax(0, 1fr) 36px', sm: 'minmax(0, 1fr) minmax(0, 1.2fr) 36px' };
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      height: 40,
      borderRadius: '8px',
      backgroundColor: '#fff',
      fontSize: '0.85rem',
      color: '#334155',
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#94a3b8' },
    },
  };
  return <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2 }}>
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', p: { xs: 2, sm: 2.5 }, backgroundColor: '#fff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#0f172a', fontSize: '1rem', fontWeight: 700 }}>Windows</Typography>
            <Typography component="span" sx={{ px: 1, py: 0.25, borderRadius: '6px', backgroundColor: '#f1f5f9', color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>{activeCount}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#64748b', fontSize: '0.8rem' }}>Name each service point and choose its default payment device.</Typography>
        </Box>
        <Button color="primary" variant="outlined" size="small" startIcon={<AddIcon />} disabled={disabled || activeCount >= 50}
          onClick={() => changeRows([...rows, { name: '', is_active: true, default_payment_device_id: null }])}
          sx={{ height: 36, px: 1.5, borderRadius: '8px', textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>Add Window</Button>
      </Box>
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
        <Box sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: columns, alignItems: 'center', gap: 2, px: 1.5, py: 1.25, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          {['Window name', 'Default payment device'].map(label => <Typography key={label} sx={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 600 }}>{label}</Typography>)}
          <Box />
        </Box>
        {!activeCount && <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>No windows yet</Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#64748b' }}>Add a window to configure a service point at this location.</Typography>
        </Box>}
        {rows.map((row, index) => row.is_active && <Box key={row.id || `new-${index}`} sx={{ display: 'grid', gridTemplateColumns: columns, alignItems: 'center', gap: { xs: 1.5, sm: 2 }, p: 1.5, borderBottom: '1px solid #f1f5f9', '&:last-child': { borderBottom: 'none' }, '&:hover': { backgroundColor: '#fafbfc' } }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: { xs: 'block', sm: 'none' }, mb: 0.5, color: '#64748b' }}>Window name</Typography>
            <TextField placeholder="e.g. Window 1" value={row.name} disabled={disabled} inputProps={{ maxLength: 80, 'aria-label': 'Window name' }} onChange={event => update(index, { name: event.target.value })} size="small" fullWidth sx={fieldSx} />
          </Box>
          <Box sx={{ minWidth: 0, gridColumn: { xs: 1, sm: 'auto' } }}>
            <Typography variant="caption" sx={{ display: { xs: 'block', sm: 'none' }, mb: 0.5, color: '#64748b' }}>Default payment device</Typography>
            <TextField select value={row.default_payment_device_id || ''} SelectProps={{ displayEmpty: true, inputProps: { 'aria-label': 'Default payment device' } }} disabled={disabled} size="small" fullWidth onChange={event => update(index, { default_payment_device_id: event.target.value || null })} sx={fieldSx}>
              <MenuItem value="">No default device</MenuItem>
              {availableDevices.map(device => <MenuItem key={deviceId(device)} value={deviceId(device)}>{device.name}</MenuItem>)}
              {row.default_payment_device_id && !availableDevices.some(device => deviceId(device) === row.default_payment_device_id) && <MenuItem value={row.default_payment_device_id} disabled>Unavailable device — choose another</MenuItem>}
            </TextField>
          </Box>
          <Box sx={{ gridColumn: { xs: 2, sm: 3 }, gridRow: 1, alignSelf: { xs: 'end', sm: 'center' } }}>
            <Tooltip title="Delete window. Historical activity is retained."><span><IconButton aria-label={`Delete ${row.name || 'window'}`} size="small" disabled={disabled} onClick={() => remove(index)} sx={{ color: '#94a3b8', borderRadius: '8px', width: 36, height: 40, '&:hover': { color: '#b91c1c', backgroundColor: '#fef2f2' } }}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
          </Box>
        </Box>)}
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#64748b' }}>Changes, including deletions, are saved with Update Location.</Typography>
    </Box>
  </Box>;
}



// WEBPACK FOOTER //
// ./src/components/box-list/LocationWindowsTab.js